import React, { useState, useRef, useEffect } from 'react';
import { Palette, Check } from 'lucide-react';
import { useTheme } from '../themes/ThemeContext';

// Compact dropdown for topbar
export function ThemeSwitcherButton() {
  const { themeId, themes, switchTheme } = useTheme();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handler(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(o => !o)}
        className="w-8 h-8 rounded-full flex items-center justify-center transition-colors"
        style={{ color: 'var(--topbar-text)', background: open ? 'rgba(255,255,255,0.12)' : 'transparent' }}
        title="Switch Theme (Alt+T)"
      >
        <Palette size={17} />
      </button>
      {open && (
        <div
          className="absolute right-0 top-11 z-[200] rounded-xl shadow-2xl border p-3 w-56"
          style={{ background: 'var(--card-bg)', borderColor: 'var(--card-border)' }}
        >
          <div className="text-xs font-bold uppercase tracking-widest mb-2 px-1" style={{ color: 'var(--text-muted)' }}>
            Theme
          </div>
          <div className="space-y-0.5">
            {themes.map(t => (
              <button
                key={t.id}
                onClick={() => { switchTheme(t.id); setOpen(false); }}
                className="w-full flex items-center gap-2.5 px-2 py-1.5 rounded-lg transition-colors text-left hover:opacity-80"
                style={{ background: themeId === t.id ? 'var(--sidebar-nav-active-bg)' : 'transparent' }}
              >
                <div
                  className="w-5 h-5 rounded-full flex-shrink-0 border-2"
                  style={{ background: t.sidebar.bg, borderColor: t.sidebar.accent }}
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>{t.name}</div>
                </div>
                {themeId === t.id && <Check size={13} className="text-green-500 flex-shrink-0" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Full theme cards for Settings → Appearance tab
export function ThemeCards() {
  const { themeId, themes, switchTheme } = useTheme();

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
      {themes.map(t => {
        const active = themeId === t.id;
        return (
          <button
            key={t.id}
            onClick={() => switchTheme(t.id)}
            className="text-left rounded-xl overflow-hidden transition-all hover:shadow-lg"
            style={{
              border: `2px solid ${active ? t.sidebar.accent : '#e5e7eb'}`,
              boxShadow: active ? `0 0 0 3px ${t.sidebar.accent}22` : undefined,
            }}
          >
            {/* Mini preview */}
            <div className="flex h-24">
              {/* Sidebar strip */}
              <div className="w-12 h-full flex-shrink-0 flex flex-col pt-2 px-1.5 gap-1" style={{ background: t.sidebar.bg }}>
                <div className="w-6 h-6 rounded-md flex-shrink-0 mb-1" style={{ background: t.sidebar.logoBg }} />
                {[1, 2, 3, 4].map(n => (
                  <div
                    key={n}
                    className="h-1.5 rounded-sm"
                    style={{
                      background: n === 1 ? t.sidebar.accent : 'rgba(255,255,255,0.2)',
                      width: n === 1 ? '100%' : n === 2 ? '80%' : n === 3 ? '70%' : '60%',
                      borderLeft: n === 1 ? `2px solid ${t.sidebar.accent}` : undefined,
                    }}
                  />
                ))}
              </div>
              {/* Content area */}
              <div className="flex-1 p-2 flex flex-col gap-1.5" style={{ background: t.main.bg }}>
                <div className="h-4 rounded w-full" style={{ background: t.main.topbarBg, opacity: 0.9 }} />
                <div
                  className="flex-1 rounded p-1.5"
                  style={{ background: t.main.cardBg, border: `1px solid ${t.main.cardBorder}` }}
                >
                  <div className="h-1.5 rounded mb-1 w-3/4" style={{ background: t.main.accent, opacity: 0.7 }} />
                  <div className="h-1 rounded w-1/2" style={{ background: t.main.cardBorder }} />
                  <div className="h-1 rounded mt-0.5 w-2/3" style={{ background: t.main.cardBorder }} />
                </div>
              </div>
            </div>
            {/* Label */}
            <div className="px-3 py-2.5 bg-white border-t" style={{ borderColor: '#e5e7eb' }}>
              <div className="flex items-center justify-between gap-2">
                <div>
                  <div className="text-sm font-semibold text-gray-800">{t.name}</div>
                  <div className="text-xs text-gray-400">{t.description}</div>
                </div>
                {active ? (
                  <span
                    className="flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0"
                    style={{ background: `${t.sidebar.accent}20`, color: t.sidebar.accent }}
                  >
                    <Check size={11} /> Active
                  </span>
                ) : (
                  <span className="text-xs text-gray-400 flex-shrink-0">Select</span>
                )}
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}
